/**
 * SSE (Server-Sent Events) Service for MLOps Agent
 * 
 * This service handles communication with the AI agent backend using
 * Server-Sent Events for real-time streaming responses.
 */

import { ENVMAPPING } from "utility/helper";
import { store } from "api-store/store";
import { reauthenticateOnForbidden } from "./handleAuthError";

// SSE API URL for the MLOps Agent backend
const getSSEApiUrl = (): string => {
    return `${ENVMAPPING(globalThis.location.origin).agentApi}agent/stream`;
};

// Get authorization token from Redux store
const getAuthToken = (): string | null => {
  const state = store.getState();
  return state.userData?.access_token || null;
};

export type Message = {
  from: "user" | "bot";
  text: string;
};

export type MessageCallback = (chunk: string) => void;
export type ConnectionCallback = (ready: boolean) => void;
export type StreamEndCallback = () => void;
export type ErrorCallback = (error: string) => void;

// SSE event types matching backend events
enum SSEEventType {
  CONNECTION = "connection",
  AGENT_START = "agent_start",
  AGENT_CHUNK = "agent_chunk",
  AGENT_RESPONSE = "agent_response",
  AGENT_COMPLETE = "agent_complete",
  AGENT_ERROR = "agent_error",
  AGENT_THINKING = "agent_thinking",
}

// Request structure for agent requests
interface AgentRequest {
  query: string;
  context?: Record<string, unknown>;
  user_id?: string;
  session_id?: string;
  stream: boolean;
}

// SSE event data structures
interface SSEConnectionData {
  status: string;
  request_id: string;
}

interface SSEStartData {
  request_id: string;
  status: string;
  query: string;
  timestamp: string;
}

interface SSEChunkData {
  request_id: string;
  chunk_index: number;
  content: string;
  is_final: boolean;
  timestamp: string;
}

interface SSEResponseData {
  request_id: string;
  status: string;
  response: string;
  timestamp: string;
}

interface SSECompleteData {
  request_id: string;
  status: string;
  timestamp: string;
}

interface SSEErrorData {
  request_id: string;
  status: string;
  error: string;
  error_type?: string;
  recoverable?: boolean;
  timestamp: string;
}

class MlopsAgentSseService {
  private eventSource: EventSource | null = null;
  private abortController: AbortController | null = null;
  private onMessageCallback: MessageCallback | null = null;
  private onConnectionCallback: ConnectionCallback | null = null;
  private onStreamEndCallback: StreamEndCallback | null = null;
  private onErrorCallback: ErrorCallback | null = null;
  private userId: string | null = null;
  private sessionId: string | null = null;
  private isConnected: boolean = false;
  private currentRequestId: string | null = null;

  /**
   * Initialize the service with callbacks
   */
  connect(
    onMessage: MessageCallback,
    onConnection: ConnectionCallback,
    onStreamEnd?: StreamEndCallback,
    userId?: string,
    sessionId?: string,
    onError?: ErrorCallback
  ): void {
    this.onMessageCallback = onMessage;
    this.onConnectionCallback = onConnection;
    this.onStreamEndCallback = onStreamEnd || null;
    this.onErrorCallback = onError || null;
    this.userId = userId || null;
    this.sessionId = sessionId || null;

    // SSE is ready when the service is initialized
    // Actual connection happens per-request via POST
    this.isConnected = true;
    this.onConnectionCallback?.(true);
    console.log("[MLOpsAgent SSE] Service initialized");
  }

  /**
   * Send a message/query to the AI agent using SSE POST endpoint
   */
  async sendMessage(query: string, context?: Record<string, unknown>): Promise<boolean> {
    if (!this.isConnected) {
      console.error("[MLOpsAgent SSE] Service not initialized");
      return false;
    }

    // Cancel any existing request
    this.cancelCurrentRequest();

    const request: AgentRequest = {
      query: query,
      context: context,
      user_id: this.userId || undefined,
      session_id: this.sessionId || undefined,
      stream: true,
    };

    try {
      this.abortController = new AbortController();
      
      console.log("[MLOpsAgent SSE] Sending request:", query.substring(0, 100));

      const doRequest = (token: string | null): Promise<Response> => {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        return fetch(getSSEApiUrl(), {
          method: "POST",
          headers,
          body: JSON.stringify(request),
          signal: this.abortController!.signal,
        });
      };

      let response = await doRequest(getAuthToken());

      // API Gateway returns 403 when the token is stale/expired. Try a silent
      // forced refresh and retry the request once.
      if (response.status === 403) {
        const refreshedToken = await reauthenticateOnForbidden();
        if (refreshedToken) {
          response = await doRequest(refreshedToken);
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }

      // Process the SSE stream
      await this.processSSEStream(response);
      return true;
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        console.log("[MLOpsAgent SSE] Request was cancelled");
        return false;
      }
      console.error("[MLOpsAgent SSE] Failed to send message:", error);
      this.onErrorCallback?.((error as Error).message);
      return false;
    }
  }

  /**
   * Process the SSE stream from a fetch response
   */
  private async processSSEStream(response: Response): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Response body is not readable");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log("[MLOpsAgent SSE] Stream completed");
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages from buffer
        const messages = this.parseSSEMessages(buffer);
        buffer = messages.remaining;

        for (const message of messages.events) {
          this.handleSSEEvent(message.event, message.data);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Parse SSE messages from a buffer
   * SSE format: event: <event>\ndata: <data>\n\n
   */
  private parseSSEMessages(buffer: string): {
    events: Array<{ event: string; data: string }>;
    remaining: string;
  } {
    const events: Array<{ event: string; data: string }> = [];
    const lines = buffer.split("\n");
    
    let currentEvent = "message";
    let currentData = "";
    let processedIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        currentData += line.slice(5).trim();
      } else if (line.startsWith("id:")) {
        // Message ID - can be used for reconnection tracking
      } else if (line.startsWith("retry:")) {
        // Retry time - can be used for reconnection
      } else if (line.startsWith(":")) {
        // Comment/keepalive - ignore
      } else if (line === "") {
        // Empty line marks end of message
        if (currentData) {
          events.push({ event: currentEvent, data: currentData });
          currentEvent = "message";
          currentData = "";
        }
        processedIndex = i + 1;
      }
    }

    // Return remaining unprocessed buffer
    const remaining = lines.slice(processedIndex).join("\n");
    return { events, remaining };
  }

  /**
   * Handle individual SSE events
   */
  private handleSSEEvent(event: string, data: string): void {
    try {
      const parsedData = JSON.parse(data);
      console.log("[MLOpsAgent SSE] Received event:", event);

      switch (event) {
        case SSEEventType.CONNECTION:
          this.handleConnectionEvent(parsedData as SSEConnectionData);
          break;

        case SSEEventType.AGENT_START:
          this.handleAgentStartEvent(parsedData as SSEStartData);
          break;

        case SSEEventType.AGENT_CHUNK:
          this.handleAgentChunkEvent(parsedData as SSEChunkData);
          break;

        case SSEEventType.AGENT_RESPONSE:
          this.handleAgentResponseEvent(parsedData as SSEResponseData);
          break;

        case SSEEventType.AGENT_COMPLETE:
          this.handleAgentCompleteEvent(parsedData as SSECompleteData);
          break;

        case SSEEventType.AGENT_ERROR:
          this.handleAgentErrorEvent(parsedData as SSEErrorData);
          break;

        case SSEEventType.AGENT_THINKING:
          // Silently ignore thinking keepalive events — not shown in chat
          break;

        default:
          console.log("[MLOpsAgent SSE] Unhandled event type:", event);
      }
    } catch (error) {
      console.error("[MLOpsAgent SSE] Failed to parse event data:", error);
    }
  }

  /**
   * Handle connection established event
   */
  private handleConnectionEvent(data: SSEConnectionData): void {
    console.log("[MLOpsAgent SSE] Connection established:", data.request_id);
    this.currentRequestId = data.request_id;
  }

  /**
   * Handle agent processing start event
   */
  private handleAgentStartEvent(data: SSEStartData): void {
    console.log("[MLOpsAgent SSE] Processing started:", data.request_id);
  }

  /**
   * Handle streaming chunk event
   */
  private handleAgentChunkEvent(data: SSEChunkData): void {
    if (data.content) {
      this.onMessageCallback?.(data.content);
    }

    if (data.is_final) {
      console.log("[MLOpsAgent SSE] Final chunk received");
      this.onStreamEndCallback?.();
      this.currentRequestId = null;
    }
  }

  /**
   * Handle complete response event (non-streaming)
   */
  private handleAgentResponseEvent(data: SSEResponseData): void {
    if (data.response) {
      this.onMessageCallback?.(data.response);
    }
    this.onStreamEndCallback?.();
    this.currentRequestId = null;
  }

  /**
   * Handle processing complete event
   */
  private handleAgentCompleteEvent(data: SSECompleteData): void {
    console.log("[MLOpsAgent SSE] Processing completed:", data.request_id);
    this.onStreamEndCallback?.();
    this.currentRequestId = null;
  }

  /**
   * Handle error event
   */
  private handleAgentErrorEvent(data: SSEErrorData): void {
    console.error("[MLOpsAgent SSE] Error:", data.error_type, data.error);
    
    // Send error message to chat (user-friendly message from backend)
    this.onMessageCallback?.(data.error);
    
    // Call error callback for additional handling (toast, etc.)
    this.onErrorCallback?.(data.error);
    
    this.onStreamEndCallback?.();
    this.currentRequestId = null;
  }

  /**
   * Cancel the current request
   */
  private cancelCurrentRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.currentRequestId = null;
  }

  async clearSession(): Promise<void> {
    if (!this.sessionId) { return; }

    try {
      const url = `${ENVMAPPING(globalThis.location.origin).agentApi}agent/session/${encodeURIComponent(this.sessionId)}`;
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(url, { method: "DELETE", headers });
      if (response.status === 403) {
        const refreshedToken = await reauthenticateOnForbidden();
        if (refreshedToken) {
          headers["Authorization"] = `Bearer ${refreshedToken}`;
          const retry = await fetch(url, { method: "DELETE", headers });
          if (retry.ok) {
            console.info("[MLOpsAgent SSE] Session cleared on backend");
          } else {
            console.info(`[MLOpsAgent SSE] Failed to clear session: ${retry.status}`);
          }
          return;
        }
      }
      if (response.ok) {
        console.info("[MLOpsAgent SSE] Session cleared on backend");
      } else {
        console.info(`[MLOpsAgent SSE] Failed to clear session: ${response.status}`);
      }
    } catch (error) {
      console.info("[MLOpsAgent SSE] Error clearing session:", error);
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect(): void {
    this.cancelCurrentRequest();
    this.isConnected = false;
    this.onConnectionCallback?.(false);
    console.log("[MLOpsAgent SSE] Service disconnected");
  }

  /**
   * Check if service is connected/ready
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Check if there's an active request
   */
  hasActiveRequest(): boolean {
    return this.currentRequestId !== null;
  }
}

export const mlopsAgentSseService = new MlopsAgentSseService();
export default mlopsAgentSseService;